import {ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild} from '@angular/core';
import {
  DOCUMENT_OBJECT_CLASSES,
  FILE_OBJECT_CLASSES,
  getMembersWithPermissionsFormControls,
  MembersWithPermissionsTableComponent,
  PermissionSettingsTableComponent,
  SharedFolderCreateUpdateDialogData,
  SharedFolderSelectorComponent,
  SharedFolderSelectorView,
  TransactionHistoryType,
  TransactionsComponent,
  WizardComponent,
  WizardStepDirective
} from '|shared';
import {ApiSharedFolderService, DocumentDto, FileDto, SharedFolderDto} from '|api/document';
import {EsslApplicationModule, OrganizationalStructureEntityType, SharedFolderPermissionLevel} from '|api/commons';
import {Observable} from 'rxjs';
import {AuthorizedEntityType} from '|api/core';
import {
  ButtonComponent,
  FormButtonsComponent,
  InaccessibleDirective,
  LoadingIndicatorService
} from '@icz/angular-essentials';
import {
  CheckboxComponent,
  DatePickerComponent,
  FormFieldComponent,
  IczFormArray,
  IczFormContainerDirective,
  IczFormControl,
  IczFormGroup,
  IczValidators
} from '@icz/angular-form-elements';
import {DialogService, IczModalService, IczModalSizeClass, injectModalData, injectModalRef} from '@icz/angular-modal';
import {ReactiveFormsModule} from '@angular/forms';
import {AsyncPipe} from '@angular/common';
import {
  CheckUnsavedFormDialogService,
  enumToOptions,
  IczFormSubmitDirective,
  IFormGroupCheckable
} from '@icz/angular-application-core';
import {
  convertViewFiltersToDataFilters,
  EMPTY_FILTER_TREE,
  FilterItem,
  FilterItemTree,
  FilterNameService,
  FilterOperator,
  FilterParam
} from '@icz/angular-table';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslatePipe} from '@ngx-translate/core';
import {
  SharedFolderFilterDefinitionDialogComponent,
  SharedFolderFilterDefinitionDialogData,
  SharedFolderFilterDefinitionDialogResult
} from '|modules/shared-folders/modules/shared-folder-filter-definition-dialog/shared-folder-filter-definition-dialog.component';
import {CurrentSessionService, EsslApplicationModulesService, isAdmin, SearchApiService} from '|ui-essl-core';

enum SharedFolderWizardStep {
  SHARED_FOLDER_SPECIFICATION = 'SHARED_FOLDER_SPECIFICATION',
  SHARED_FOLDER_PERMISSIONS = 'SHARED_FOLDER_PERMISSIONS',
  SHARED_FOLDER_HISTORY = 'SHARED_FOLDER_HISTORY',
}

@Component({
  selector: 'icz-create-update-shared-folder-dialog',
  templateUrl: './create-update-shared-folder-dialog.component.html',
  styleUrls: ['./create-update-shared-folder-dialog.component.scss'],
  providers: [
    CheckUnsavedFormDialogService
  ],
  imports: [WizardComponent, InaccessibleDirective, WizardStepDirective, IczFormContainerDirective, ReactiveFormsModule, FormFieldComponent, SharedFolderSelectorComponent, PermissionSettingsTableComponent, TransactionsComponent, FormButtonsComponent, ButtonComponent, IczFormSubmitDirective, AsyncPipe, MembersWithPermissionsTableComponent, DatePickerComponent, CheckboxComponent, TranslatePipe]
})
export class CreateUpdateSharedFolderDialogComponent implements OnInit, IFormGroupCheckable {

  protected loadingService = inject(LoadingIndicatorService);
  private modalRef = injectModalRef<Nullable<SharedFolderDto>>();
  private destroyRef = inject(DestroyRef);
  private checkUnsavedService = inject(CheckUnsavedFormDialogService);
  private currentSessionService = inject(CurrentSessionService);
  private apiSharedFolderService = inject(ApiSharedFolderService);
  protected dialogData = injectModalData<SharedFolderCreateUpdateDialogData>();
  protected filterNameService = inject(FilterNameService);
  protected iczModalService = inject(IczModalService);
  protected changeDetectorRef = inject(ChangeDetectorRef);
  private esslApplicationModulesService = inject(EsslApplicationModulesService);
  private dialogService = inject(DialogService);

  @ViewChild(WizardComponent)
  wizardComponent!: WizardComponent;

  isNew = true;
  showAutoFillOptions = false;
  humanReadableFilter = '';
  activeFilters: Nullable<FilterItemTree>;
  searchTerm: Nullable<string>;

  form = new IczFormGroup({
    name: new IczFormControl<Nullable<string>>(null, [IczValidators.required()]),
    parentId: new IczFormControl<Nullable<number>>(null, [IczValidators.required()]),
    autoFilled: new IczFormControl<boolean>(false),
    filterPredicates: new IczFormControl<Nullable<string>>(null, []),
    ...getMembersWithPermissionsFormControls(),
  });

  sharedFolderPermissionOptions = enumToOptions('sharedFolderPermissionLevel', SharedFolderPermissionLevel);

  formGroupsToCheck!: string[];

  get isAtSharedFolderSpecificationStep() {
    return this.wizardComponent?.currentStep.id === SharedFolderWizardStep.SHARED_FOLDER_SPECIFICATION;
  }

  get isAutomationModuleActive() {
    return this.esslApplicationModulesService.isEsslApplicationModuleActive(EsslApplicationModule.AUTOMATION);
  }

  get sharedFolder() {
    return this.dialogData.sharedFolder;
  }

  readonly SharedFolderWizardStep = SharedFolderWizardStep;

  ngOnInit(): void {
    this.checkUnsavedService.addUnsavedFormCheck(this, ['form']);

    if (this.dialogData.isCreateMode) {
      this.form.patchValue({
        parentId: this.sharedFolder?.id,
      });

      const permissionsControl = this.form.get('functionalPositions') as IczFormArray;
      const adminPermissionControl = permissionsControl.incrementSize();

      adminPermissionControl.patchValue({
        id: this.currentSessionService.currentUserFunctionalPosition!.id,
        code: this.currentSessionService.currentUserFunctionalPosition!.code,
        name: this.currentSessionService.currentUserFunctionalPosition!.name,
        permission: SharedFolderPermissionLevel.COMPLETE_CONTROL,
        isReadonly: true,
      });
    }
    else {
      this.isNew = false;
      this.form.patchValue({
        name: this.sharedFolder!.name,
        parentId: this.sharedFolder!.parentId,
        autoFilled: this.sharedFolder!.autoFilled!,
        filterPredicates: this.sharedFolder!.filterPredicates,
      });
    }

    if (isAdmin(this.currentSessionService.currentUserFunctionalPosition!)) {
      this.form.get('parentId')!.clearValidators();
      this.form.updateValueAndValidity();
      this.showAutoFillOptions = this.isAutomationModuleActive;
      if (!this.isNew) {
        this.humanReadableFilter = this.sharedFolder!.humanReadableFilter!;
      }
    }
  }

  selectObjects() {
    this.iczModalService.openComponentInModal<Nullable<SharedFolderFilterDefinitionDialogResult>, SharedFolderFilterDefinitionDialogData>({
      component: SharedFolderFilterDefinitionDialogComponent,
      modalOptions: {
        sizeClass: IczModalSizeClass.WH_RESPONSIVE,
        titleTemplate: 'Vyberte kritéria výběru objektů',
        disableAutoMargin: true
      },
      data: {
        activeFilters: this.activeFilters,
        searchTerm: this.searchTerm,
      },
    }).subscribe(result => {
      if (result && (result.searchTerm || result.activeFilters)) {
        this.searchTerm = result.searchTerm;
        this.activeFilters = result.activeFilters;

        this.filterItemTreeToString(result.activeFilters, result.searchTerm);

        let dataFilters: FilterParam[] = [];
        let filterPredicates = '';

        if (result.searchTerm) {
          filterPredicates += `text=${result.searchTerm}`;
        }

        if (result.activeFilters.values.length) {
          dataFilters = convertViewFiltersToDataFilters(result.activeFilters.values as FilterItem[]);
        }

        const objectClassFieldName = 'objectClass' satisfies keyof (DocumentDto | FileDto);
        const isObjectClassUserSpecified = dataFilters.some(f => f.fieldName === objectClassFieldName);

        if (!isObjectClassUserSpecified) {
          dataFilters.push({
            fieldName: objectClassFieldName,
            operator: FilterOperator.inSet,
            value: String([...DOCUMENT_OBJECT_CLASSES, ...FILE_OBJECT_CLASSES]),
          });
        }

        const filterQueryParams = SearchApiService.getFilterQueryParams({
          filter: dataFilters,
        });

        if (filterPredicates) {
          filterPredicates += '&';
        }

        filterPredicates += String(SearchApiService.searchApiParamsToHttpParams(filterQueryParams));

        this.form.get('filterPredicates')!.setValue(filterPredicates);
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  private filterItemTreeToString(filters: Nullable<FilterItemTree>, searchTerm: Nullable<string>) {
    this.filterNameService.getFilterName(filters ?? EMPTY_FILTER_TREE, searchTerm, true).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(humanReadableFiltersValue => {
      this.humanReadableFilter = humanReadableFiltersValue!;
    });
  }

  goToPermissionsStep() {
    this.wizardComponent.nextStep();
  }

  submit() {
    const formValue = this.form.getRawValue();
    let request$: Observable<SharedFolderDto>;

    if (formValue.autoFilled && isNil(formValue.filterPredicates)) {
      this.dialogService.showError('Sdílená složka nemá definovaná kritéria pro výběr objektů automatického plnění.');
    } else {
      if (this.isNew) {
        request$ = this.apiSharedFolderService.sharedFolderCreate({
          body: {
            name: formValue.name!,
            parentId: formValue.parentId,
            autoFilled: formValue.autoFilled,
            filterPredicates: formValue.filterPredicates,
            humanReadableFilter: this.humanReadableFilter,
            sharing: formValue.functionalPositions
              .filter(
                fpPermissionAssignment => fpPermissionAssignment.id !== this.currentSessionService.currentUserFunctionalPosition!.id
              ).map(fpPermissionAssignment => ({
                sharedWith: [
                  {
                    organizationalStructureEntityType: OrganizationalStructureEntityType.FUNCTIONAL_POSITION,
                    organizationalStructureEntityId: fpPermissionAssignment.id!,
                  }
                ],
                permissionSetLevel: fpPermissionAssignment.permission as SharedFolderPermissionLevel,
                revoking: false,
              })),
          },
        });
      }
      else {
        request$ = this.apiSharedFolderService.sharedFolderUpdate({
          id: this.sharedFolder!.id,
          body: {
            name: formValue.name!,
            parentId: formValue.parentId,
            autoFilled: formValue.autoFilled,
            filterPredicates: formValue.filterPredicates,
            humanReadableFilter: this.humanReadableFilter,
          },
        });
      }

      this.loadingService.doLoading(
        request$,
        this,
      ).subscribe(sharedFolder => {
        this.modalRef.close(sharedFolder);
      });
    }
  }

  cancel() {
    this.modalRef.close(null);
  }

  protected readonly AuthorizedEntityType = AuthorizedEntityType;
  protected readonly SharedFolderSelectorView = SharedFolderSelectorView;
  protected readonly TransactionHistoryType = TransactionHistoryType;
}
